<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import { GitBranch, X } from "lucide-vue-next";
  import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";

  const surveyTargetsStore = useSurveyTargetsStore();
  const {
    selectedTargetIndex,
    currentTargetsConditions,
    conditionQuestionModel,
    conditionQuestionOptions,
    typeOptions,
    operatorOptions,
    selectedTargetValidationErrors,
  } = storeToRefs(surveyTargetsStore);
  const { updateConditionQuestion, updateConditionType, conditionValuesOptions, updateConditionValues, updateOperator, handleAddCondition, removeCondition } =
    surveyTargetsStore;

  const conditions = computed(() => currentTargetsConditions.value[selectedTargetIndex.value] ?? []);
  const questionModel = computed(() => conditionQuestionModel.value[selectedTargetIndex.value] ?? []);
  const conditionsError = computed(() => selectedTargetValidationErrors.value.find((e) => e.name === "conditions")?.message);
</script>

<template>
  <div class="space-y-2">
    <Label>Conditions</Label>

    <div class="space-y-1.5">
      <div
        v-for="(condition, i) in conditions"
        :key="condition.token"
        class="flex items-center gap-2">
        <div class="w-20 shrink-0">
          <span
            v-if="i === 0"
            class="block px-3 text-13 font-semibold text-primary">
            If
          </span>
          <Select
            v-else
            :model-value="conditions[i - 1].operator"
            :options="operatorOptions"
            class="h-9 font-semibold text-primary"
            @update:model-value="updateOperator(conditions[i - 1], $event)" />
        </div>
        <Select
          class="h-9 min-w-0 flex-[5]"
          :model-value="questionModel[i]"
          :options="conditionQuestionOptions"
          placeholder="Question"
          filterable
          @update:model-value="updateConditionQuestion(condition, $event)" />
        <Select
          class="h-9 w-24 shrink-0"
          :model-value="condition.type"
          :options="typeOptions"
          @update:model-value="updateConditionType(condition, $event)" />
        <Select
          class="min-h-9 min-w-0 flex-[4]"
          :model-value="condition.values"
          :options="conditionValuesOptions(condition)"
          multiple
          filterable
          placeholder="Select value(s)"
          @update:model-value="updateConditionValues(condition, $event)" />
        <Button
          variant="ghost"
          size="icon-sm"
          class="shrink-0 text-foreground-subtle hover:text-primary"
          aria-label="Remove this condition"
          @click="removeCondition(selectedTargetIndex, i)">
          <X class="size-3.5" />
        </Button>
      </div>
    </div>

    <div v-if="conditionsError">
      <Badge
        variant="destructive"
        class="text-xs">
        {{ conditionsError }}
      </Badge>
    </div>

    <Button
      class="mt-2 w-fit"
      variant="outline"
      @click="handleAddCondition(selectedTargetIndex)">
      <GitBranch class="size-4" />
      Add condition
    </Button>
  </div>
</template>
